import React, { useState, useEffect } from 'react'
import { Card, Typography, Input, Button, message, Tag, Space, Spin, Row, Col, Alert, Dropdown, Avatar, Divider } from 'antd'
import { UserOutlined, LockOutlined, DollarOutlined, CheckCircleOutlined, LogoutOutlined, InstagramOutlined, HomeOutlined, TrophyOutlined, MessageOutlined, FireOutlined, DownOutlined, CheckOutlined, SmileOutlined } from '@ant-design/icons'
import { portalAPI, phrasesAPI, attendanceAPI, wellbeingAPI } from '../services/api'
import { motion, AnimatePresence } from 'framer-motion'
import dayjs from 'dayjs'
import 'dayjs/locale/es'
dayjs.locale('es')

const { Title, Text } = Typography

const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } }
const stagger = (i) => ({ initial: { opacity: 0, y: 15 }, animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.08, duration: 0.3 } })

const cardBase = { borderRadius: 16, border: '1px solid #222', background: '#141414' }
const cardGlow = { ...cardBase, border: '1px solid rgba(255,255,255,0.25)', boxShadow: '0 0 20px rgba(255,255,255,0.08)' }

const GOLD = '#e0b34a'
const GOLD_SOFT = 'rgba(224,179,74,0.14)'

const weekOrder = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5 }
const dayNames = { monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles', thursday: 'Jueves', friday: 'Viernes' }
const dayLabelsShort = ['L', 'M', 'X', 'J', 'V']

const MOODS = [
  { key: 'energia', label: 'Con mucha energía', emoji: '⚡' },
  { key: 'bien', label: 'Bien', emoji: '🙂' },
  { key: 'masomenos', label: 'Más o menos', emoji: '😐' },
  { key: 'bajoneada', label: 'Bajoneada', emoji: '😔' },
  { key: 'cansada', label: 'Cansada', emoji: '😴' }
]
const MOOD_MSGS = {
  energia: '¡Aprovechá esa energía! Hoy es un gran día para superarte.',
  bien: '¡Genial! Un poquito cada día también es progreso.',
  masomenos: 'No todos los días tenemos la misma energía. Lo importante es seguir.',
  bajoneada: 'Está bien no estar bien. Hoy hacé lo que puedas, sin exigirte de más.',
  cansada: 'Escuchá a tu cuerpo. Descansar también es parte del progreso.'
}

function ClientPortal() {
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [loading, setLoading] = useState(false)
  const [nameInput, setNameInput] = useState('')
  const [lastName, setLastName] = useState('')
  const [code, setCode] = useState('')
  const [profile, setProfile] = useState(null)
  const [payments, setPayments] = useState([])
  const [classes, setClasses] = useState([])
  const [attendance, setAttendance] = useState([])
  const [profMessage, setProfMessage] = useState('')
  const [membership, setMembership] = useState(null)
  const [routines, setRoutines] = useState([])
  const [dataLoading, setDataLoading] = useState(false)
  const [showEditTurnos, setShowEditTurnos] = useState(false)
  const [view, setView] = useState('inicio')
  const [showRoutine, setShowRoutine] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [dailyPhrase, setDailyPhrase] = useState('')
  const [moodToday, setMoodToday] = useState(null)
  const [moodSel, setMoodSel] = useState(null)
  const [moodComment, setMoodComment] = useState('')
  const [moodSubmitted, setMoodSubmitted] = useState(false)
  const [moodSaving, setMoodSaving] = useState(false)
  const [habitsToday, setHabitsToday] = useState([])
  const [daySummary, setDaySummary] = useState(null)
  const [catSel, setCatSel] = useState('funcional')
  const [verMas, setVerMas] = useState(false)
  const [showSplash, setShowSplash] = useState(true)
  const [attSaving, setAttSaving] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setShowSplash(false), 3500)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    const token = localStorage.getItem('clientToken')
    if (token) { setIsLoggedIn(true); loadData(token) }
    loadPhrase()
    const params = new URLSearchParams(window.location.search)
    const qrToken = params.get('qr')
    const autoCheckin = params.get('checkin')
    if (qrToken) {
      localStorage.setItem('pendingQR', qrToken)
      window.history.replaceState({}, '', '/portal')
    }
    if (autoCheckin === 'auto') {
      localStorage.setItem('pendingAutoCheckin', 'true')
      window.history.replaceState({}, '', '/portal')
    }
  }, [])

  const loadPhrase = async () => { try { const r = await phrasesAPI.getToday(); setDailyPhrase(r.data.phrase || '') } catch (e) {} }

  const handleLogin = async () => {
    if (!nameInput || !lastName || !code) return message.warning('Completa todos los campos')
    setLoading(true)
    try {
      const res = await portalAPI.login(nameInput, lastName, code)
      localStorage.setItem('clientToken', res.data.token)
      setProfile(res.data.client); setIsLoggedIn(true); loadData(res.data.token)
    } catch (error) { message.error(error.response?.data?.error || 'Error') }
    finally { setLoading(false) }
  }

  const loadData = async (token) => {
    setDataLoading(true)
    const config = { headers: { Authorization: `Bearer ${token || localStorage.getItem('clientToken')}` } }
    try {
      const [p, pay, c, a, m, msg, rt] = await Promise.all([
        portalAPI.getProfile(config), portalAPI.getPayments(config), portalAPI.getClasses(config),
        portalAPI.getAttendance(config), portalAPI.getMembership(config), portalAPI.getMessage(config), portalAPI.getRoutines(config)
      ])
      setProfile(p.data); setPayments(pay.data); setClasses(c.data); setAttendance(a.data)
      setMembership(m.data); setProfMessage(msg.data?.text || ''); setRoutines(rt.data || [])
      try {
        const [mo, hb, sm] = await Promise.all([
          wellbeingAPI.getMoodToday(config), wellbeingAPI.getHabitsToday(config), wellbeingAPI.getSummary(config)
        ])
        setMoodToday(mo.data?.mood ? { mood: mo.data.mood, comment: mo.data.comment } : null)
        setMoodSel(mo.data?.mood || null)
        setMoodSubmitted(!!mo.data?.mood)
        setHabitsToday(hb.data || [])
        setDaySummary(sm.data || null)
      } catch (e) { /* si falla el módulo de bienestar no rompemos el resto */ }
      const pendingQR = localStorage.getItem('pendingQR')
      if (pendingQR && p.data.id) {
        try {
          const res = await attendanceAPI.qrCheckIn(pendingQR, p.data.id)
          if (res.data.success) message.success('Asistencia registrada!')
          else message.info(res.data.message || 'Ya registrada')
        } catch (e) { message.error(e.response?.data?.error || 'Error con QR') }
        localStorage.removeItem('pendingQR')
      }
      const pendingAuto = localStorage.getItem('pendingAutoCheckin')
      if (pendingAuto && p.data.id) {
        try {
          const res = await attendanceAPI.autoCheckIn(p.data.id)
          if (res.data.success) message.success('Asistencia registrada! ' + (res.data.className || ''))
          else message.info(res.data.message || 'Ya registrada hoy')
        } catch (e) { message.error(e.response?.data?.error || 'Error') }
        localStorage.removeItem('pendingAutoCheckin')
      }
    } catch (e) { if (e.response?.status === 401) handleLogout() }
    finally { setDataLoading(false) }
  }

  const handleLogout = () => { localStorage.removeItem('clientToken'); setIsLoggedIn(false); setProfile(null) }
  const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('clientToken')}` } })
  const handleEnroll = async (id) => { try { await portalAPI.enroll(id, authConfig()); message.success('Anotada!'); loadData() } catch (e) { message.error(e.response?.data?.error || 'Error') } }
  const handleUnenroll = async (id) => { try { await portalAPI.unenroll(id, authConfig()); message.success('Saliste'); loadData() } catch (e) { message.error(e.response?.data?.error || 'Error') } }

  const handleRegisterAttendance = async (classId) => {
    if (attSaving) return
    setAttSaving(true)
    try {
      const res = await portalAPI.registerAttendance(classId, authConfig())
      message.success(res.data.message || 'Asistencia registrada!')
      loadData()
    } catch (e) { message.error(e.response?.data?.error || 'Error al registrar') }
    finally { setAttSaving(false) }
  }

  const todayDayName = dayjs().format('dddd').toLowerCase()
  const dayMap = { lunes: 'monday', martes: 'tuesday', miercoles: 'wednesday', jueves: 'thursday', viernes: 'friday' }

  // SPLASH
  if (showSplash) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#0a0a0a', padding: 24, position: 'relative' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} style={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
          <Text style={{
            color: '#fff', fontSize: 24, fontWeight: 800, letterSpacing: 6, display: 'block', marginBottom: 20,
            textTransform: 'uppercase',
            textShadow: '0 0 20px rgba(255,255,255,0.8), 0 0 40px rgba(255,255,255,0.4), 0 2px 6px rgba(0,0,0,0.6)'
          }}>Bienvenidos a</Text>
          <motion.img
            src="/screenkeka.png"
            alt=""
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
            style={{ width: '100%', maxWidth: 300, borderRadius: 18, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.15)', boxShadow: '0 0 60px rgba(255,255,255,0.18)', margin: '0 auto' }}
          />
          <Text style={{
            display: 'block', marginTop: 26,
            fontSize: 40, fontWeight: 900, letterSpacing: 6,
            background: 'linear-gradient(180deg, #fff7d6 0%, #ffe08a 30%, #E0B34A 60%, #b8860b 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 0 22px rgba(224,179,74,0.65)) drop-shadow(0 0 55px rgba(224,179,74,0.35)) drop-shadow(0 3px 8px rgba(0,0,0,0.7))',
            paddingBottom: 8
          }}>ESPÍRITU LIBRE</Text>
        </motion.div>
      </div>
    )
  }

  // LOGIN
  if (!isLoggedIn) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#0a0a0a', padding: 16, position: 'relative' }}>
        <Button type="link" size="small" style={{ position: 'absolute', top: 12, right: 12, color: '#ffffff', fontSize: 11, fontWeight: 600, letterSpacing: 1, border: '1px solid #ffffff', borderRadius: 8, padding: '4px 12px', background: 'rgba(255,255,255,0.12)', boxShadow: '0 0 14px rgba(255,255,255,0.25)', zIndex: 10 }} href="/login">
          <LockOutlined style={{ fontSize: 10 }} /> ADMIN
        </Button>
        <motion.div {...fadeUp} style={{ width: '100%', maxWidth: 380 }}>
          <Card style={{ ...cardGlow, padding: 24 }}>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <img src="/logobox.jpg" alt="" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid #ffffff', boxShadow: '0 0 24px rgba(255,255,255,0.3)' }} />
              <Title level={3} style={{ color: '#ffffff', marginTop: 16, marginBottom: 4, letterSpacing: 2 }}>ESPÍRITU LIBRE</Title>
              <Text style={{ color: '#666', fontSize: 13 }}>Portal de Alumnas</Text>
            </div>
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
              <Input size="large" prefix={<UserOutlined style={{ color: '#555' }} />} placeholder="Tu nombre" value={nameInput} onChange={(e) => setNameInput(e.target.value)} style={{ borderRadius: 12 }} />
              <Input size="large" prefix={<UserOutlined style={{ color: '#555' }} />} placeholder="Tu apellido" value={lastName} onChange={(e) => setLastName(e.target.value)} style={{ borderRadius: 12 }} />
              <Input.Password size="large" prefix={<LockOutlined style={{ color: '#555' }} />} placeholder="Codigo personal" value={code} onChange={(e) => setCode(e.target.value)} onPressEnter={handleLogin} style={{ borderRadius: 12 }} />
              <Button type="primary" size="large" block onClick={handleLogin} loading={loading} style={{ borderRadius: 12, height: 48, fontSize: 16 }}>Entrar</Button>
            </Space>
          </Card>
        </motion.div>
      </div>
    )
  }

  if (dataLoading && !profile) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />

  const enrolledClasses = classes.filter(c => c.isEnrolled)
  const enrolledDays = new Set(enrolledClasses.map(c => c.dayOfWeek)).size
  const getPlan = (d) => { if (d > 3) return { l: 'Completa', p: 35000 }; if (d === 3) return { l: '3x sem', p: 30000 }; if (d === 2) return { l: '2x sem', p: 25000 }; return { l: '-', p: 0 } }
  const plan = membership?.planName ? { l: membership.planName, p: membership.planPrice ?? getPlan(enrolledDays).p } : getPlan(enrolledDays)
  const planPerWeek = membership?.planClassesPerWeek ?? null
  const todayEng = dayMap[todayDayName] || ''
  const hasClassToday = enrolledClasses.some(c => c.dayOfWeek === todayEng)
  const whatsappLink = `https://wa.me/5492355471129?text=${encodeURIComponent('Hola! Te envio mi comprobante de pago')}`
  const classesByDay = {}; classes.forEach(c => { const d = c.dayOfWeek || 'x'; if (!classesByDay[d]) classesByDay[d] = []; classesByDay[d].push(c) })

  // Proxima actividad
  const nextClass = (() => {
    const now = dayjs()
    const todayNum = now.day() || 7
    let best = null, bestAhead = Infinity
    enrolledClasses.forEach(c => {
      const cDay = weekOrder[c.dayOfWeek]
      if (!cDay) return
      let ahead = cDay - todayNum
      if (ahead < 0) ahead += 7
      if (ahead === 0) {
        const [h, m] = (c.startTime || '0:0').split(':').map(Number)
        if (now.hour() * 60 + now.minute() < h * 60 + m) ahead = 0
        else ahead = 7
      }
      if (ahead < bestAhead) { bestAhead = ahead; best = c }
    })
    return { cls: best, ahead: bestAhead }
  })()
  const nextLabel = nextClass.cls ? (nextClass.ahead === 0 ? `Hoy ${nextClass.cls.startTime}` : `${dayNames[nextClass.cls.dayOfWeek]} ${nextClass.cls.startTime}`) : null

  // Progreso semanal (L M X J V)
  const attendedSet = new Set(attendance.map(a => a.date))
  const monday = dayjs().startOf('week').add(1, 'day')
  const weekData = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes'].map((d, i) => ({
    key: d, label: dayLabelsShort[i],
    trained: attendedSet.has(monday.add(i, 'day').format('YYYY-MM-DD')),
    isToday: d === todayEng
  }))
  const weekCount = weekData.filter(d => d.trained).length
  const monthAttCount = attendance.filter(a => dayjs(a.date).format('YYYY-MM') === dayjs().format('YYYY-MM')).length
  const lastAtt = [...attendance].sort((a, b) => dayjs(b.date) - dayjs(a.date))[0]

  const todayEnrolledClasses = enrolledClasses.filter(c => c.dayOfWeek === todayEng)
  const alreadyRegisteredToday = attendance.some(a => dayjs(a.date).format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD'))

  const doneCount = habitsToday.filter(h => h.completed).length
  const totalCount = habitsToday.length
  const percent = daySummary?.percent ?? (totalCount ? Math.round((doneCount / totalCount) * 100) : 0)
  const allDone = totalCount > 0 && doneCount === totalCount

  // Rutinas del dia / de la alumna
  const myRoutines = routines.filter(r => !r.clientId || r.clientId === profile?.id)
  const todayRoutines = myRoutines.filter(r => (r.days || []).includes(todayEng))

  const startTraining = () => {
    if (myRoutines.length > 0) setShowRoutine(!showRoutine)
    else message.info('Tu rutina se carga pronto 💪')
  }

  const handleMoodSelect = (key) => setMoodSel(key)
  const handleMoodSubmit = async () => {
    if (!moodSel) return message.warning('Elegí una emoción')
    setMoodSaving(true)
    try {
      const res = await wellbeingAPI.postMood({ mood: moodSel, comment: moodComment.trim() || '' }, authConfig())
      setMoodToday(res.data); setMoodSubmitted(true)
      message.success('¡Gracias por contarnos! 💛')
    } catch (e) { message.error(e.response?.data?.error || 'No se pudo guardar') }
    finally { setMoodSaving(false) }
  }

  const toggleHabit = async (id) => {
    try {
      const res = await wellbeingAPI.toggleHabit(id, authConfig())
      setHabitsToday(prev => prev.map(h => h.id === id ? { ...h, completed: res.data.completed } : h))
      const sm = await wellbeingAPI.getSummary(authConfig())
      setDaySummary(sm.data)
      if (res.data.completed) message.success('Rutina completada 💪')
    } catch (e) { message.error(e.response?.data?.error || 'Error') }
  }

  const menuItems = [
    { key: 'inicio', icon: <HomeOutlined />, label: 'Inicio' },
    { key: 'midia', icon: <SmileOutlined />, label: 'Mi día' },
    { key: 'perfil', icon: <UserOutlined />, label: 'Profe' },
    { key: 'membresia', icon: <TrophyOutlined />, label: 'Mis horarios' },
    { key: 'pagos', icon: <DollarOutlined />, label: 'Historial' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Cerrar sesión' }
  ]

  const handleMenu = ({ key }) => {
    setDropdownOpen(false)
    if (key === 'logout') handleLogout()
    else setView(key)
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>

      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(10,10,10,0.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #1a1a1a', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => { setView('inicio'); setShowRoutine(false); setDropdownOpen(false) }}>
          <img src="/logobox.jpg" alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.4)' }} />
          <div>
            <Text style={{ color: '#fff', fontSize: 13, fontWeight: 'bold', letterSpacing: 1, display: 'block' }}>ESPÍRITU LIBRE</Text>
          </div>
        </div>
        <Dropdown menu={{ items: menuItems, onClick: handleMenu }} trigger={['click']} open={dropdownOpen} onOpenChange={setDropdownOpen} placement="bottomRight">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '4px 10px', borderRadius: 20, border: `1px solid ${GOLD_SOFT}`, background: '#141414' }}>
            <Avatar size={24} style={{ background: GOLD, color: '#000', fontWeight: 'bold', fontSize: 12 }}>{profile?.name?.[0]?.toUpperCase()}</Avatar>
            <Text style={{ color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>{profile?.name?.toUpperCase()}</Text>
            <DownOutlined style={{ color: GOLD, fontSize: 9 }} />
          </div>
        </Dropdown>
      </div>

      <div style={{ padding: '16px 14px 0' }}>

      <AnimatePresence mode="wait">
      <motion.div key={view} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>

      {/* ===== INICIO ===== */}
      {view === 'inicio' && (
        <div>
          {/* Saludo */}
          <motion.div {...stagger(0)}>
            <div style={{ marginBottom: 18 }}>
              <Text style={{ color: GOLD, fontSize: 11, fontWeight: 700, letterSpacing: 3, display: 'block' }}>{dayjs().format('dddd DD MMM').toUpperCase()}</Text>
              <Title level={2} style={{ margin: '6px 0 2px', color: '#ffffff', fontWeight: 800, letterSpacing: 1 }}>
                HOLA, {profile?.name?.toUpperCase()}
              </Title>
              <Text style={{ color: '#b8b8b8', fontSize: 15, fontWeight: 500 }}>¿Lista para entrenar?</Text>
            </div>
          </motion.div>

          {/* Check-in emocional */}
          <motion.div {...stagger(1)}>
            <Card style={{ ...cardBase, padding: 16, marginBottom: 14, borderColor: 'rgba(255,255,255,0.15)' }}>
              <Text style={{ color: GOLD, fontSize: 10, fontWeight: 700, letterSpacing: 2, display: 'block', marginBottom: 4 }}>ANTES DE COMENZAR…</Text>
              <Text style={{ color: '#fff', fontSize: 16, fontWeight: 800, display: 'block', marginBottom: 2 }}>¿Cómo te sentís hoy?</Text>
              <Text style={{ color: '#888', fontSize: 12, display: 'block', marginBottom: 14 }}>Contanos cómo estás. No hay respuestas correctas.</Text>
              {!moodSubmitted ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, marginBottom: 12 }}>
                    {MOODS.map(m => (
                      <div key={m.key} onClick={() => handleMoodSelect(m.key)} style={{ cursor: 'pointer', textAlign: 'center', flex: 1 }}>
                        <div style={{ width: 44, height: 44, borderRadius: '50%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, background: '#1c1c1c', border: moodSel === m.key ? `2px solid ${GOLD}` : '1px solid #333', boxShadow: moodSel === m.key ? '0 0 12px rgba(224,179,74,0.4)' : 'none', transition: 'all 0.2s' }}>
                          {m.emoji}
                        </div>
                        <Text style={{ color: moodSel === m.key ? '#fff' : '#777', fontSize: 8.5, display: 'block', marginTop: 4, lineHeight: 1.2, fontWeight: moodSel === m.key ? 700 : 400 }}>{m.label}</Text>
                      </div>
                    ))}
                  </div>
                  {moodSel && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
                      <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 12, padding: 12, marginBottom: 10 }}>
                        <Text style={{ color: '#fff', fontSize: 13, fontStyle: 'italic', display: 'block', marginBottom: 10 }}>"{MOOD_MSGS[moodSel]}"</Text>
                        <Input.TextArea rows={2} placeholder="¿Querés contarle algo a la profe? Hoy me siento…" value={moodComment} onChange={(e) => setMoodComment(e.target.value)} style={{ borderRadius: 10, background: '#111', borderColor: '#333', color: '#fff' }} />
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <Button type="primary" size="small" style={{ flex: 1, borderRadius: 8, background: GOLD, borderColor: GOLD, color: '#000', fontWeight: 700 }} loading={moodSaving} onClick={handleMoodSubmit}>
                            Contarle a la profe
                          </Button>
                          <Button size="small" style={{ borderRadius: 8 }} loading={moodSaving} onClick={handleMoodSubmit}>Solo la emoción</Button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: GOLD_SOFT, border: `1px solid ${GOLD}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                    {MOODS.find(m => m.key === (moodToday?.mood || moodSel))?.emoji || '💛'}
                  </div>
                  <div>
                    <Text style={{ color: '#fff', fontSize: 14, fontWeight: 700, display: 'block' }}>¡Gracias por contarnos! 💛</Text>
                    <Text style={{ color: '#888', fontSize: 12 }}>{moodToday?.comment ? 'Tu mensaje ya llegó a tu profe.' : 'Tu profe sabe cómo te sentís hoy.'}</Text>
                  </div>
                </div>
              )}
            </Card>
          </motion.div>

          {/* Mi día (compacto) */}
          <motion.div {...stagger(2)}>
            <Card style={{ ...cardGlow, padding: 16, marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <Text style={{ color: '#fff', fontSize: 13, fontWeight: 700, letterSpacing: 1 }}>MI DÍA</Text>
                <Text style={{ color: GOLD, fontSize: 14, fontWeight: 800 }}>{percent}%</Text>
              </div>
              <Text style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 10 }}>Pequeños hábitos, grandes cambios.</Text>
              <div style={{ height: 8, borderRadius: 6, background: '#1c1c1c', overflow: 'hidden', marginBottom: 10 }}>
                <motion.div animate={{ width: `${percent}%` }} transition={{ duration: 0.5 }} style={{ height: '100%', borderRadius: 6, background: GOLD }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={{ color: '#ccc', fontSize: 12 }}>
                  {totalCount ? `${doneCount} de ${totalCount} objetivos cumplidos` : 'Cargando tus objetivos…'}
                </Text>
                <Button size="small" type="link" style={{ padding: 0, fontSize: 12, color: GOLD, fontWeight: 700 }} onClick={() => setView('midia')}>
                  Ver mi día →
                </Button>
              </div>
              {daySummary?.streak > 0 && (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Text style={{ fontSize: 16 }}>🔥</Text>
                  <Text style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{daySummary.streak} {daySummary.streak === 1 ? 'día' : 'días'} cuidándote</Text>
                </div>
              )}
            </Card>
          </motion.div>

          {/* Mensaje de la profe */}
          <motion.div {...stagger(3)}>
            <Card style={{ ...cardGlow, padding: 16, marginBottom: 14, borderLeft: `3px solid ${GOLD}`, background: 'linear-gradient(135deg, rgba(224,179,74,0.08), rgba(20,20,20,0.9) 55%)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <Avatar size={34} style={{ background: GOLD, color: '#000', fontWeight: 'bold', flexShrink: 0 }} icon={<MessageOutlined />} />
                <Text style={{ color: GOLD, fontSize: 11, fontWeight: 700, letterSpacing: 2 }}>MENSAJE DE LA PROFE</Text>
              </div>
              {profMessage ? (
                <Text style={{ color: '#ffffff', fontSize: 15, lineHeight: 1.6, fontStyle: 'italic' }}>"{profMessage}"</Text>
              ) : (
                <Text style={{ color: '#999', fontSize: 14, fontStyle: 'italic' }}>"Todavía no hay mensajes nuevos. Volvé a chequear más tarde."</Text>
              )}
            </Card>
          </motion.div>

          {/* Proxima clase + Hoy */}
          <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
            <Col span={12}>
              <motion.div {...stagger(4)} style={{ height: '100%' }}>
                <Card style={{ ...cardBase, padding: 14, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Text style={{ color: '#999', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, display: 'block', marginBottom: 10 }}>PRÓXIMA CLASE</Text>
                  {nextClass.cls ? (
                    <>
                      <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold', display: 'block' }}>
                        {nextClass.ahead === 0 ? 'Hoy' : dayNames[nextClass.cls.dayOfWeek]}
                      </Text>
                      <Text style={{ color: GOLD, fontSize: 18, fontWeight: 800, display: 'block', margin: '2px 0 4px' }}>{nextClass.cls.startTime}</Text>
                      <Tag color="#ffffff" style={{ color: '#000', borderRadius: 8, width: 'fit-content', fontSize: 10, fontWeight: 700, padding: '0 8px', alignSelf: 'flex-start' }}>
                        {(nextClass.cls.type || 'CLASE').toUpperCase()}
                      </Tag>
                      <Button size="small" type="link" style={{ padding: 0, marginTop: 'auto', fontSize: 12, color: '#ccc' }} onClick={() => setView('membresia')}>
                        Ver horario →
                      </Button>
                    </>
                  ) : (
                    <>
                      <Text style={{ color: '#888', fontSize: 13, display: 'block', marginBottom: 8 }}>Sin horarios elegidos</Text>
                      <Button size="small" type="link" style={{ padding: 0, marginTop: 'auto', fontSize: 12, color: GOLD, fontWeight: 700 }} onClick={() => setView('membresia')}>
                        Elegir horario →
                      </Button>
                    </>
                  )}
                </Card>
              </motion.div>
            </Col>
            <Col span={12}>
              <motion.div {...stagger(5)} style={{ height: '100%' }}>
                <Card style={{ ...cardBase, padding: 14, height: '100%', display: 'flex', flexDirection: 'column', borderColor: hasClassToday ? GOLD_SOFT : '#222' }}>
                  <Text style={{ color: '#999', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, display: 'block', marginBottom: 10 }}>HOY</Text>
                  {hasClassToday ? (
                    <>
                      <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold', display: 'block' }}>
                        {enrolledClasses.find(c => c.dayOfWeek === todayEng)?.type || 'Entrenamiento'}
                      </Text>
                      <Text style={{ color: GOLD, fontSize: 18, fontWeight: 800, display: 'block', margin: '2px 0 10px' }}>
                        {enrolledClasses.find(c => c.dayOfWeek === todayEng)?.startTime} hs
                      </Text>
                      <Button type="primary" size="small" icon={<FireOutlined />} style={{ borderRadius: 10, width: '100%', background: GOLD, borderColor: GOLD, color: '#000', fontWeight: 700, letterSpacing: 1 }} onClick={startTraining}>
                        {showRoutine ? 'OCULTAR' : 'VER MI RUTINA'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold', display: 'block' }}>Día de descanso</Text>
                      <Text style={{ color: '#888', fontSize: 12, display: 'block', margin: '2px 0 10px' }}>Recargá energía para mañana</Text>
                      <Button size="small" type="link" style={{ padding: 0, marginTop: 'auto', fontSize: 12, color: '#ccc' }} onClick={startTraining}>
                        Ver mi rutina
                      </Button>
                    </>
                  )}
                </Card>
              </motion.div>
            </Col>
          </Row>

          {/* Rutina desplegable */}
          <AnimatePresence>
          {showRoutine && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ overflow: 'hidden' }}>
              {myRoutines.length === 0 ? (
                <Card style={{ ...cardBase, padding: 16, marginBottom: 14, textAlign: 'center' }}>
                  <Text style={{ color: '#666' }}>Tu rutina se carga pronto. Preguntale a la profe 💪</Text>
                </Card>
              ) : (
                myRoutines.map((r, i) => {
                  const isToday = (r.days || []).includes(todayEng)
                  return (
                    <Card key={r.id} style={{ ...cardBase, padding: 16, marginBottom: 12, borderColor: isToday ? 'rgba(255,255,255,0.4)' : '#222' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Text style={{ color: '#fff', fontSize: 15, fontWeight: 'bold' }}>{r.name} {isToday && <Tag color="#ffffff" style={{ color: '#000', borderRadius: 20 }}>HOY</Tag>}</Text>
                      </div>
                      {r.description && <Text style={{ color: '#888', fontSize: 12, display: 'block', marginBottom: 6 }}>{r.description}</Text>}
                      <div style={{ marginBottom: 6 }}>
                        {(r.days || []).map(d => <Tag key={d} style={{ background: '#1a1a1a', border: '1px solid #333', color: '#aaa', borderRadius: 20 }}>{dayNames[d] || d}</Tag>)}
                      </div>
                      {(r.exercises || []).length > 0 && (
                        <ol style={{ margin: 0, paddingLeft: 18 }}>
                          {(r.exercises || []).map((e, idx) => <li key={idx}><Text style={{ color: '#ccc', fontSize: 13 }}>{e}</Text></li>)}
                        </ol>
                      )}
                    </Card>
                  )
                })
              )}
            </motion.div>
          )}
          </AnimatePresence>

          {/* Progreso semanal */}
          <motion.div {...stagger(6)}>
            <Card style={{ ...cardGlow, padding: 16, marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <Text style={{ color: '#fff', fontSize: 13, fontWeight: 700, letterSpacing: 1 }}>TU PROGRESO</Text>
                <Text style={{ color: GOLD, fontSize: 15, fontWeight: 800 }}>{weekCount} / 5</Text>
              </div>
              <Text style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 12 }}>
                {weekCount === 1 ? 'entrenamiento' : 'entrenamientos'} esta semana
              </Text>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                {weekData.map((d) => (
                  <div key={d.key} style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: '50%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: d.trained ? GOLD : '#1c1c1c',
                      border: d.isToday && !d.trained ? `1px solid ${GOLD}` : '1px solid #333',
                      boxShadow: d.trained ? '0 0 14px rgba(224,179,74,0.35)' : 'none'
                    }}>
                      {d.trained ? <CheckCircleOutlined style={{ color: '#000', fontSize: 15 }} /> : <Text style={{ color: d.isToday ? GOLD : '#555', fontSize: 12, fontWeight: 700 }}>{d.label}</Text>}
                    </div>
                    <Text style={{ color: d.isToday ? '#fff' : '#666', fontSize: 9, fontWeight: d.isToday ? 700 : 400, display: 'block', marginTop: 4 }}>{d.isToday ? 'HOY' : ''}</Text>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>

          {/* Asistencia */}
          <motion.div {...stagger(7)}>
            <Card style={{ ...cardBase, padding: 16, marginBottom: 14 }}>
              <Text style={{ color: '#fff', fontSize: 13, fontWeight: 700, letterSpacing: 1, display: 'block', marginBottom: 10 }}>MIS ASISTENCIAS</Text>
              {attendance.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '6px 0 2px' }}>
                  <Text style={{ color: '#777', fontSize: 13, display: 'block', marginBottom: 8 }}>Todavía no tenés asistencias registradas</Text>
                  <Button size="small" type="link" style={{ padding: 0, fontSize: 12, color: GOLD, fontWeight: 700 }} onClick={() => setView('perfil')}>
                    Ver historial →
                  </Button>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                    <Text style={{ color: GOLD, fontSize: 32, fontWeight: 800, lineHeight: 1 }}>{monthAttCount}</Text>
                    <Text style={{ color: '#ccc', fontSize: 13 }}>{monthAttCount === 1 ? 'entrenamiento realizado' : 'entrenamientos realizados'} este mes</Text>
                  </div>
                  {lastAtt && (
                    <Text style={{ color: '#888', fontSize: 12, display: 'block', marginBottom: 8 }}>
                      Última asistencia: {dayjs(lastAtt.date).format('dddd DD/MM').replace(/^\w/, l => l.toUpperCase())}
                    </Text>
                  )}
                  <Button size="small" type="link" style={{ padding: 0, fontSize: 12, color: GOLD, fontWeight: 700 }} onClick={() => setView('perfil')}>
                    Ver historial →
                  </Button>
                </div>
              )}
              {!alreadyRegisteredToday && (
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #222' }}>
                  <Text style={{ color: '#fff', fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 10 }}>REGISTRAR ASISTENCIA DE HOY</Text>
                  {todayEnrolledClasses.length === 0 ? (
                    <Text style={{ color: '#777', fontSize: 12 }}>No tenés clases para hoy. Igual podés registrar tu entrenamiento.</Text>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {todayEnrolledClasses.map(c => (
                        <Button
                          key={c.id}
                          block
                          size="middle"
                          style={{ borderRadius: 10, height: 'auto', padding: '10px 12px', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1a1a1a', border: '1px solid #333', color: '#fff' }}
                          onClick={() => handleRegisterAttendance(c.id)}
                        >
                          <span>{c.type === 'spinning' ? 'Spinning' : 'Funcional'} · {c.startTime} a {c.endTime}</span>
                          <CheckCircleOutlined style={{ color: GOLD }} />
                        </Button>
                      ))}
                    </div>
                  )}
                  <Button
                    type="primary"
                    block
                    size="middle"
                    loading={attSaving}
                    style={{ borderRadius: 10, marginTop: 8, background: GOLD, borderColor: GOLD, color: '#000', fontWeight: 700 }}
                    onClick={() => handleRegisterAttendance(null)}
                  >
                    Registrar asistencia de hoy
                  </Button>
                </div>
              )}
            </Card>
          </motion.div>

          {/* Historial pagos */}
          <motion.div {...stagger(8)}>
            <Card style={{ ...cardBase, padding: 16, marginBottom: 14 }}>
              <Text style={{ color: '#fff', fontSize: 12, display: 'block', marginBottom: 10 }}>Historial de pagos</Text>
              {payments.length === 0 ? (
                <Text style={{ color: '#444', fontSize: 12 }}>Sin pagos registrados</Text>
              ) : (
                <div>
                  {payments.slice(0, 3).map((p, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #1a1a1a' }}>
                      <Text style={{ color: '#ccc', fontSize: 12 }}>${p.amount} — {dayjs(p.paymentDate).format('DD/MM/YY')}</Text>
                      <Tag color={p.status === 'paid' ? '#52c41a' : '#faad14'} style={{ borderRadius: 12, margin: 0 }}>{p.status === 'paid' ? 'Pagado' : 'Pendiente'}</Tag>
                    </div>
                  ))}
                  {payments.length > 3 && <Text style={{ color: '#555', fontSize: 11, marginTop: 6, display: 'block' }}>Ver todos en Pagos</Text>}
                </div>
              )}
            </Card>
          </motion.div>

          {/* Frase del dia */}
          {dailyPhrase && (
            <motion.div {...stagger(9)}>
              <Card style={{ ...cardBase, padding: 14, marginBottom: 14, textAlign: 'center' }}>
                <Text style={{ color: '#cccccc', fontSize: 13, fontStyle: 'italic' }}>"{dailyPhrase}"</Text>
              </Card>
            </motion.div>
          )}
        </div>
      )}

      {/* ===== MI DÍA ===== */}
      {view === 'midia' && (
        <div>
          <Title level={4} style={{ color: '#ffffff', marginBottom: 16 }}>Mi día</Title>

          <Card style={{ ...cardGlow, padding: 16, marginBottom: 14 }}>
            <Text style={{ color: GOLD, fontSize: 11, fontWeight: 700, letterSpacing: 2, display: 'block', marginBottom: 4 }}>PEQUEÑOS HÁBITOS, GRANDES CAMBIOS</Text>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <Text style={{ color: '#fff', fontSize: 17, fontWeight: 800 }}>Tu día va…</Text>
              <Text style={{ color: GOLD, fontSize: 24, fontWeight: 800 }}>{percent}%</Text>
            </div>
            <div style={{ height: 10, borderRadius: 6, background: '#1c1c1c', overflow: 'hidden', marginBottom: 8 }}>
              <motion.div animate={{ width: `${percent}%` }} transition={{ duration: 0.5 }} style={{ height: '100%', borderRadius: 6, background: GOLD }} />
            </div>
            <Text style={{ color: '#999', fontSize: 12 }}>{doneCount} de {totalCount} objetivos cumplidos</Text>
          </Card>

          {allDone && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}>
              <Card style={{ padding: 16, marginBottom: 14, borderRadius: 16, border: '1px solid rgba(224,179,74,0.5)', background: 'linear-gradient(135deg, rgba(224,179,74,0.15), rgba(20,20,20,0.9))', textAlign: 'center' }}>
                <Text style={{ color: '#fff', fontSize: 20, fontWeight: 800, display: 'block' }}>¡Día completo! 🎉</Text>
                <Text style={{ color: '#ccc', fontSize: 13, display: 'block', marginTop: 4 }}>Hoy hiciste algo por vos. Mañana seguimos.</Text>
              </Card>
            </motion.div>
          )}

          {totalCount === 0 ? (
            <Card style={{ ...cardBase, padding: 16, marginBottom: 14, textAlign: 'center' }}>
              <Text style={{ color: '#666', fontSize: 13 }}>Hoy no hay hábitos cargados. ¡Disfrutá tu día! 💛</Text>
            </Card>
          ) : (
            <div>
              {habitsToday.map((h, i) => (
                <motion.div key={h.id} {...stagger(i)}>
                  <Card
                    style={{ ...cardBase, padding: 14, marginBottom: 10, cursor: 'pointer', borderColor: h.completed ? 'rgba(224,179,74,0.6)' : '#222', background: h.completed ? 'linear-gradient(135deg, rgba(224,179,74,0.1), #141414)' : '#141414', transition: 'all 0.2s' }}
                    onClick={() => toggleHabit(h.id)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 38, height: 38, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, background: h.completed ? GOLD : '#1c1c1c', border: h.completed ? 'none' : '1px solid #333' }}>
                        {h.completed ? <CheckOutlined style={{ color: '#000', fontSize: 16 }} /> : h.emoji}
                      </div>
                      <Text style={{ color: h.completed ? '#fff' : '#ddd', fontSize: 15, fontWeight: h.completed ? 700 : 500, flex: 1 }}>{h.title}</Text>
                      {h.completed && <Text style={{ color: GOLD, fontSize: 11, fontWeight: 700 }}>✓ HECHO</Text>}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}

          {daySummary?.streak > 0 && (
            <Card style={{ ...cardBase, padding: 16, textAlign: 'center' }}>
              <Text style={{ fontSize: 28, display: 'block' }}>🔥</Text>
              <Text style={{ color: '#fff', fontSize: 18, fontWeight: 800, display: 'block' }}>{daySummary.streak} {daySummary.streak === 1 ? 'día' : 'días'} cuidándote</Text>
              <Text style={{ color: '#888', fontSize: 12 }}>Constancia: {daySummary.daysCompleted} días completos</Text>
            </Card>
          )}
        </div>
      )}

      {/* ===== MEMBRESIA ===== */}
      {view === 'membresia' && (
        <div>
          <Title level={4} style={{ color: '#ffffff', marginBottom: 16 }}>Mi membresía</Title>

          <Card style={{ ...cardGlow, padding: 20, marginBottom: 14, textAlign: 'center' }}>
            <Text style={{ color: '#888', fontSize: 11, letterSpacing: 1 }}>MI PLAN</Text>
            <Text style={{ color: '#fff', fontSize: 26, fontWeight: 'bold', display: 'block', marginTop: 6 }}>{plan.l}</Text>
            <Text style={{ color: '#888', fontSize: 14, display: 'block' }}>${plan.p.toLocaleString()}/mes{planPerWeek ? ` · ${planPerWeek}x por semana` : ''}</Text>
            <Divider style={{ borderColor: '#222', margin: '14px 0' }} />
            <Text style={{ color: '#888', fontSize: 12, display: 'block', marginBottom: 8 }}>Estado de la cuota</Text>
            <Tag color={profile?.status === 'active' ? '#52c41a' : '#ff4d4f'} style={{ fontSize: 13, borderRadius: 12, padding: '4px 14px' }}>
              {profile?.status === 'active' ? 'Al día' : 'Cuota vencida'}
            </Tag>
            <Text style={{ color: '#555', fontSize: 12, display: 'block', marginTop: 10 }}>
              Vence el {dayjs(profile?.expirationDate).format('DD/MM/YYYY')}
              {membership?.daysLeft != null && membership.daysLeft >= 0 && ` · ${membership.daysLeft} días restantes`}
            </Text>
          </Card>

          <Card style={{ ...cardBase, padding: 16, marginBottom: 14 }}>
            <Text style={{ color: '#fff', fontSize: 12, display: 'block', marginBottom: 10 }}>Mis horarios</Text>
            {enrolledClasses.length > 0 ? (
              ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map(day => {
                const my = enrolledClasses.filter(c => c.dayOfWeek === day)
                if (!my.length) return null
                return <div key={day} style={{ marginBottom: 6 }}><Text style={{ color: '#fff', fontSize: 14 }}><strong style={{ color: '#ffffff' }}>{dayNames[day]}</strong> — {my.map(c => c.startTime + '-' + c.endTime).join(', ')}</Text></div>
              })
            ) : (
              <Text style={{ color: '#666', fontSize: 13 }}>Todavía no elegiste horarios</Text>
            )}
          </Card>

          <Button type="primary" block onClick={() => setShowEditTurnos(!showEditTurnos)} style={{ borderRadius: 12, marginBottom: 14 }}>
            {showEditTurnos ? 'Ocultar horarios' : enrolledClasses.length > 0 ? 'Cambiar turnos' : 'Elegir horarios'}
          </Button>

          {showEditTurnos && (
            <div>
              {planPerWeek && enrolledDays >= planPerWeek && <Alert style={{ marginBottom: 10, borderRadius: 10 }} type="warning" showIcon message={`Tu plan ${plan.l} incluye ${planPerWeek} clases por semana (${plan.p.toLocaleString()}/mes). Estás en el límite de tu plan.`} />}
              {!planPerWeek && enrolledDays === 1 && <Alert style={{ marginBottom: 10, borderRadius: 10 }} type="warning" showIcon message="Otro día = 2x sem ($25.000)" />}
              {!planPerWeek && enrolledDays === 2 && <Alert style={{ marginBottom: 10, borderRadius: 10 }} type="warning" showIcon message="Otro día = 3x sem ($30.000) +$5.000" />}
              {!planPerWeek && enrolledDays === 3 && <Alert style={{ marginBottom: 10, borderRadius: 10 }} type="warning" showIcon message="Otro día = Completa ($35.000) +$5.000" />}
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map(day => {
                const dc = (classesByDay[day] || []).sort((a, b) => a.startTime.localeCompare(b.startTime))
                return (
                  <div key={day} style={{ marginBottom: 14 }}>
                    <Text style={{ color: '#ffffff', fontSize: 14, fontWeight: '600' }}>{dayNames[day]}</Text>
                    {dc.map(item => (
                      <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: 12, marginTop: 6, background: item.isEnrolled ? '#0f2a0f' : '#141414', border: item.isEnrolled ? '1px solid #52c41a' : '1px solid #222' }}>
                        <Text style={{ color: '#fff', fontSize: 14 }}>{item.startTime} - {item.endTime} <span style={{ color: '#555', fontSize: 11 }}>({item.enrolled}/{item.capacity})</span></Text>
                        {item.isEnrolled ? <Button size="small" danger style={{ borderRadius: 8 }} onClick={() => handleUnenroll(item.id)}>Salir</Button> : <Button size="small" type="primary" style={{ borderRadius: 8 }} onClick={() => handleEnroll(item.id)} disabled={item.enrolled >= item.capacity || profile?.status !== 'active'}>Elegir</Button>}
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== PAGOS ===== */}
      {view === 'pagos' && (
        <div>
          <Title level={4} style={{ color: '#ffffff', marginBottom: 16 }}>Pagos</Title>
          <Card style={{ ...cardBase, padding: 16, marginBottom: 14, textAlign: 'center' }}>
            <Text style={{ color: '#888', fontSize: 12 }}>Transferencia</Text>
            <Text style={{ color: '#fff', fontSize: 18, fontWeight: 'bold', display: 'block', marginTop: 6 }}>espiritu.libre.22</Text>
            <Button type="primary" block style={{ marginTop: 12, borderRadius: 12, background: '#25d366', borderColor: '#25d366' }} href={whatsappLink} target="_blank">Enviar comprobante</Button>
          </Card>
          <Card style={{ ...cardBase, padding: 16 }}>
            <Text style={{ color: '#888', fontSize: 12, display: 'block', marginBottom: 10 }}>Historial</Text>
            {payments.length === 0 ? <Text style={{ color: '#444' }}>Sin pagos</Text> : payments.map((p, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #1a1a1a' }}>
                <div><Text style={{ color: '#fff', fontSize: 13 }}>${p.amount}</Text><br /><Text style={{ color: '#555', fontSize: 11 }}>{dayjs(p.paymentDate).format('DD/MM/YY')}</Text></div>
                <Tag color={p.status === 'paid' ? '#52c41a' : '#faad14'} style={{ height: 'fit-content', borderRadius: 12 }}>{p.status === 'paid' ? 'Pagado' : 'Pendiente'}</Tag>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* ===== PERFIL ===== */}
      {view === 'perfil' && (
        <div>
          <Title level={4} style={{ color: '#ffffff', marginBottom: 16 }}>Perfil</Title>

          <Card style={{ ...cardGlow, padding: 20, marginBottom: 14, textAlign: 'center' }}>
            <motion.div animate={{ rotate: [0, 14, -8, 14, -4, 10, 0] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 4 }} style={{ display: 'inline-block', marginBottom: 8 }}>
              <img src="/keka.png" alt="" style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${GOLD}`, boxShadow: '0 0 30px rgba(224,179,74,0.35)' }} />
            </motion.div>
            <Title level={5} style={{ color: '#fff', margin: '8px 0 2px' }}>TU PROFE</Title>
            <Text style={{ color: '#cccccc', fontSize: 13 }}>Keka Guevara</Text>
            <Text style={{ color: '#999', fontSize: 12, display: 'block', marginTop: 4 }}>Entrenadora de funcional y spinning</Text>
            <Text style={{ color: '#888', fontSize: 12, display: 'block', marginTop: 8, lineHeight: 1.6 }}>Entrenamientos dinámicos adaptados a todos los niveles, enfocados en mejorar tu resistencia, fuerza y bienestar general.</Text>
            <Row gutter={[8, 8]} style={{ marginTop: 12 }}>
              <Col span={8}><Text style={{ color: '#ffffff', fontWeight: 'bold', display: 'block' }}>+5</Text><Text style={{ color: '#999', fontSize: 10 }}>años</Text></Col>
              <Col span={8}><Text style={{ color: '#ffffff', fontWeight: 'bold', display: 'block' }}>+50</Text><Text style={{ color: '#999', fontSize: 10 }}>alumnas</Text></Col>
              <Col span={8}><Text style={{ color: '#ffffff', fontWeight: 'bold', display: 'block' }}>100%</Text><Text style={{ color: '#999', fontSize: 10 }}>personal</Text></Col>
            </Row>
            <Space style={{ marginTop: 14 }}>
              <Button size="small" style={{ borderRadius: 8, background: '#25d366', borderColor: '#25d366', color: '#fff' }} href="https://wa.me/5492355471129" target="_blank">WhatsApp</Button>
              <Button size="small" style={{ borderRadius: 8, background: '#262626', borderColor: '#444', color: '#fff' }} href="https://www.instagram.com/kekaguevara?igsh=M2JzcmdmYjdldG4=" target="_blank"><InstagramOutlined /> IG</Button>
            </Space>
          </Card>

          <Card style={{ ...cardBase, padding: 16, marginBottom: 14 }}>
            <Text style={{ color: '#aaa', fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>UBICACIÓN</Text>
            <Text style={{ color: '#fff', fontSize: 15, fontWeight: 600, display: 'block', marginTop: 6 }}>📍 Calle 22 N°646</Text>
            <Text style={{ color: '#888', fontSize: 12 }}>Ciudad Florentino Ameghino, Buenos Aires</Text>
          </Card>

          <Card style={{ ...cardBase, padding: 16, marginBottom: 14 }}>
            <Text style={{ color: GOLD, fontSize: 10, fontWeight: 700, letterSpacing: 2, display: 'block', marginBottom: 2 }}>BENEFICIOS DE ENTRENAR</Text>
            <Title level={4} style={{ color: '#fff', margin: '2px 0 12px', fontWeight: 800 }}>Explorar Categorías</Title>

            <Row gutter={[10, 10]}>
              <Col span={12}>
                <div onClick={() => setCatSel('funcional')} style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', height: 96, cursor: 'pointer', background: '#1c1c1c', border: `2px solid ${catSel === 'funcional' ? GOLD : 'transparent'}`, boxShadow: catSel === 'funcional' ? '0 0 16px rgba(224,179,74,0.45)' : 'none', transition: 'all 0.25s' }}>
                  <img src="/funcional.jpg" alt="Funcional" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.85))', padding: '28px 10px 8px', textAlign: 'center' }}>
                    <Text style={{ color: GOLD, fontSize: 13, fontWeight: 800, letterSpacing: 2, display: 'block', textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>FUNCIONAL</Text>
                    <Text style={{ color: '#ddd', fontSize: 10, display: 'block', marginTop: 2 }}>Mejorar fuerza</Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div onClick={() => setCatSel('spinning')} style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', height: 96, cursor: 'pointer', background: '#1c1c1c', border: `2px solid ${catSel === 'spinning' ? GOLD : 'transparent'}`, boxShadow: catSel === 'spinning' ? '0 0 16px rgba(224,179,74,0.45)' : 'none', transition: 'all 0.25s' }}>
                  <img src="/spinning.jpg" alt="Spinning" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.85))', padding: '28px 10px 8px', textAlign: 'center' }}>
                    <Text style={{ color: GOLD, fontSize: 13, fontWeight: 800, letterSpacing: 2, display: 'block', textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>SPINNING</Text>
                    <Text style={{ color: '#ddd', fontSize: 10, display: 'block', marginTop: 2 }}>Cardio intenso</Text>
                  </div>
                </div>
              </Col>
            </Row>

            <Text style={{ color: '#ccc', fontSize: 13, lineHeight: 1.7, display: 'block', marginTop: 12 }}>
              {catSel === 'funcional'
                ? 'Fuerza, coordinación y resistencia con ejercicios de peso corporal y materiales. Entrenamientos dinámicos para todos los niveles.'
                : 'Cardio intenso en bicicleta fija: quemás calorías, fortalecés piernas y liberás tensiones. Ideal para dar todo en menos tiempo.'}
            </Text>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 12 }}>
              {[['⚡', 'Resistencia'], ['💪', 'Tonificación'], ['🧘‍♀️', 'Menos estrés']].map(([e, l]) => (
                <div key={l} style={{ background: '#1c1c1c', border: '1px solid #2a2a2a', borderRadius: 12, padding: '10px 6px', textAlign: 'center' }}>
                  <Text style={{ fontSize: 20, display: 'block' }}>{e}</Text>
                  <Text style={{ color: '#ddd', fontSize: 11, display: 'block', marginTop: 4, fontWeight: 600 }}>{l}</Text>
                </div>
              ))}
            </div>

            <Button size="small" type="link" style={{ padding: 0, marginTop: 12, fontSize: 12, color: GOLD, fontWeight: 700 }} onClick={() => setVerMas(!verMas)}>
              {verMas ? 'Ver menos −' : 'Ver más →'}
            </Button>
            {verMas && (
              <Text style={{ color: '#aaa', fontSize: 12, lineHeight: 1.7, display: 'block', marginTop: 8 }}>
                En Espiritu Libre vas a mejorar tu resistencia, tonificar, liberar estrés y ganar confianza. Cada clase es un espacio para vos, sin importar tu nivel.
              </Text>
            )}
          </Card>

          <Button block danger style={{ borderRadius: 12 }} onClick={handleLogout}>Cerrar sesión</Button>
        </div>
      )}

      </motion.div>
      </AnimatePresence>

      <div style={{ textAlign: 'center', padding: '16px 12px 20px', marginTop: 20 }}>
        <Text style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 3 }}>Desarrollado por <a href="https://www.instagram.com/impulsoweb.pv?igsh=NTNjYXM4ZWdoY3B3" target="_blank" rel="noopener noreferrer" style={{ color: '#E0B34A', fontWeight: 600, textDecoration: 'none' }}>Impulso Web PV</a></Text>
        <Text style={{ color: '#555', fontSize: 10 }}>© 2026 <a href="https://www.instagram.com/impulsoweb.pv?igsh=NTNjYXM4ZWdoY3B3" target="_blank" rel="noopener noreferrer" style={{ color: '#E0B34A', textDecoration: 'none' }}>Impulso Web PV</a></Text>
      </div>

      </div>
    </div>
  )
}

export default ClientPortal
